import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { IconButton } from '@ipfabric/ui-components/IconButton/IconButton';
import { FloatingList } from '@ipfabric/ui-components/List/FloatingList/FloatingList';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import type { TableColumnSort } from '@ipfabric/utils';
import { ApiTable } from 'components/ApiTable/components/ApiTable';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import type {
  TableBulkAction,
  TableRowIdValue,
} from 'components/Table/model/TableModel';
import { useMemo } from 'react';
import type { FC } from 'react';
import { apiEndpointToBaseUrl } from 'utils/apiEndpointToFullUrl';
import { useTableEndpoint } from 'utils/table/useTableEndpoint';
import { useSingleConfigQuery } from './queries';
import {
  type CompareManagementConfigurationConfig,
  type ManagementConfigurationConfig,
  isCompareManagementConfigurationConfig,
} from './types';

type Props = {
  onCompare: (
    configs: [
      CompareManagementConfigurationConfig,
      CompareManagementConfigurationConfig,
    ],
  ) => void;
};

export const PickConfig: FC<Props> = (props) => {
  const singleConfigQuery = useSingleConfigQuery();

  const getSelectedHashes = (selectedRows: Set<TableRowIdValue>): string[] =>
    [...selectedRows].reduce<string[]>((hashes, rowId) => {
      if (typeof rowId === 'string') {
        hashes.push(rowId);
      }

      return hashes;
    }, []);

  const bulkActions: TableBulkAction[] = [
    (state) => ({
      key: 'compare',
      title: 'Compare',
      icon: 'fa-code-compare',
      disabled: state.selectedRows?.size !== 2,
      disabledReason: 'Select exactly 2 rows to use Compare function',
      onClick: async (event) => {
        const selectedHashes = getSelectedHashes(event.selectedRows);
        if (selectedHashes.length !== 2) {
          stumble(
            `Expected to find 2 selected hash strings, found ${event.selectedRows} instead`,
          );
          return;
        }

        if (
          event.selectedData.some(
            (selectedItem) =>
              selectedItem !== undefined &&
              !isCompareManagementConfigurationConfig(selectedItem),
          )
        ) {
          stumble('Invalid selected data found.');
          return;
        }

        const configPromises = [
          // Fixed noUncheckedIndexedAccess NIM-19408
          event.selectedData[0] ?? singleConfigQuery(selectedHashes[0]!),
          event.selectedData[1] ?? singleConfigQuery(selectedHashes[1]!),
        ];

        const configResults = await Promise.all(configPromises);
        const configs = configResults.filter(
          (x) => x !== undefined,
        ) as CompareManagementConfigurationConfig[];

        if (configs.length === 2) {
          const [fromConfig, toConfig] = configs.sort(
            (a, b) => a.lastChangeAt - b.lastChangeAt,
          );

          // Fixed noUncheckedIndexedAccess NIM-19408
          props.onCompare([fromConfig!, toConfig!]);
        } else {
          stumble('Could not retrieve selected configs!');
        }
      },
    }),
  ];

  const endpoint = useTableEndpoint({
    apiEndpoint: '/tables/management/configuration',
    alternative: (origEndpoint) => ({
      id: origEndpoint.id,
      webDependencies: ['sn'],
      columns: [
        '...',
        createFrontendOnlyColumn({
          key: 'actions',
          disabledSort: true,
          title: 'Actions',
          webDependencies: ['hash', 'relatedHash'],
          render: (row: ManagementConfigurationConfig) => {
            const compareWithPreviousButton = (
              <IconButton
                type='button'
                variant='plain'
                size='small'
                icon='fa-code-compare'
                disabled={!row.relatedHash}
                onClick={async () => {
                  if (!row.relatedHash) {
                    return;
                  }

                  const related = await singleConfigQuery(row.relatedHash);
                  if (!related) {
                    return;
                  }

                  props.onCompare([row, related]);
                }}
              />
            );

            return (
              <div className='flex flex-row items-center gap-xs'>
                <FloatingList
                  items={[
                    {
                      label: 'Download full config',
                      href: apiEndpointToBaseUrl(
                        `/tables/management/configuration/download?hash=${row.hash}&sn=${row.sn}`,
                      ),
                    },
                    {
                      label: 'Download sanitized config',
                      href: apiEndpointToBaseUrl(
                        `/tables/management/configuration/download?hash=${row.hash}&sn=${row.sn}&sanitized=true`,
                      ),
                    },
                  ]}
                >
                  {(setRef, setOpen) => (
                    <IconButton
                      type='button'
                      variant='plain'
                      size='small'
                      icon='fa-download'
                      forwardRef={setRef}
                      onClick={() => {
                        setOpen(true);
                      }}
                    />
                  )}
                </FloatingList>
                {row.relatedHash ? (
                  compareWithPreviousButton
                ) : (
                  <Tooltip content='No previous configuration available'>
                    <div>{compareWithPreviousButton}</div>
                  </Tooltip>
                )}
              </div>
            );
          },
        }),
      ],
    }),
  });

  const initialSort: TableColumnSort<string> = useMemo(
    () => ({ column: 'lastChangeAt', order: 'desc' }),
    [],
  );

  return (
    <ApiTable
      rowIdField='hash'
      className='flex-1'
      endpoint={endpoint}
      initialSort={initialSort}
      bulkActions={bulkActions}
      rowSelectionFilter={{
        canRowBeSelected: (row, state) =>
          !!state &&
          (state.selectedRows.has(
            (row as ManagementConfigurationConfig).hash,
          ) ||
            state.selectedRows.size < 2),
        disableSelectAll: true,
        selectAllFilter: {},
      }}
    />
  );
};
